import React, { createContext, useContext, useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userId, setUserId] = useState('');
  const [hasPayment, setHasPayment] = useState(false);
  const [paidCategories, setPaidCategories] = useState(new Set());

  // Function to fetch user ID and full_name from backend
  const fetchUserId = async (email) => {
    try {
      console.log('Fetching user ID for email:', email);
      const response = await fetch(`${API_BASE_URL}/api/users/by-email?email=${encodeURIComponent(email)}`);
      if (response.ok) {
        const data = await response.json();
        console.log('User ID fetched successfully:', data.id);
        console.log('User full_name from backend:', data.full_name);
        return { id: data.id, full_name: data.full_name || null };
      } else {
        console.log('Failed to fetch user ID, status:', response.status);
        return null;
      }
    } catch (error) {
      console.error('Error fetching user ID:', error);
      return null;
    }
  };

  // Check for existing session on app load
  useEffect(() => {
    const savedUser = localStorage.getItem('chatbotUser');
    if (savedUser) {
      const user = JSON.parse(savedUser);
      setIsUserLoggedIn(true);
      setUserName(user.name || user.email || 'Unknown User');
      setUserEmail(user.email);
      setUserId(user.userId || '');
      setHasPayment(user.hasPayment || false);
      
      // If user is logged in but doesn't have a user ID, or if name is missing/email, fetch from backend
      if (user.email && (!user.userId || !user.name || user.name === user.email)) {
        console.log('User logged in but missing user ID or name, fetching from backend...');
        fetchUserId(user.email).then(userData => {
          if (userData) {
            setUserId(userData.id);
            // If we got full_name from backend and current name is missing or same as email, update it
            if (userData.full_name && (!user.name || user.name === user.email)) {
              console.log('Updating userName from backend full_name:', userData.full_name);
              setUserName(userData.full_name);
              const updatedUser = { ...user, userId: userData.id, name: userData.full_name };
              localStorage.setItem('chatbotUser', JSON.stringify(updatedUser));
            } else {
              // Update localStorage with the user ID only
              const updatedUser = { ...user, userId: userData.id };
              localStorage.setItem('chatbotUser', JSON.stringify(updatedUser));
            }
          }
        });
      }
    }
  }, []);

  const login = async (name, email) => {
    let displayName = name || email || 'Unknown User';
    setIsUserLoggedIn(true);
    setUserEmail(email);
    
    // Fetch user ID and full_name from backend
    const userData = await fetchUserId(email);
    if (userData) {
      setUserId(userData.id || '');
      // If backend has full_name and current name is missing or same as email, use backend's full_name
      if (userData.full_name && (!name || name === email)) {
        console.log('Using full_name from backend:', userData.full_name);
        displayName = userData.full_name;
      }
    }
    
    setUserName(displayName);
    
    localStorage.setItem('chatbotUser', JSON.stringify({ 
      name: displayName, 
      email, 
      userId: userData?.id || '', 
      hasPayment: false 
    }));
  };

  const logout = () => {
    setIsUserLoggedIn(false);
    setUserName('');
    setUserEmail('');
    setUserId('');
    setHasPayment(false);
    setPaidCategories(new Set());
    localStorage.removeItem('chatbotUser');
    localStorage.removeItem('chatbotActivePage');
  };

  const updatePaymentStatus = (hasPaymentStatus) => {
    setHasPayment(hasPaymentStatus);
    const savedUser = localStorage.getItem('chatbotUser');
    if (savedUser) {
      const user = JSON.parse(savedUser);
      user.hasPayment = hasPaymentStatus;
      localStorage.setItem('chatbotUser', JSON.stringify(user));
    }
  };

  const addPaidCategory = (categoryName) => {
    const normalizedName = (categoryName || '').toLowerCase().trim();
    setPaidCategories(prev => new Set([...prev, normalizedName]));
  };

  // Function to manually refresh user ID and full_name
  const refreshUserId = async () => {
    if (userEmail) {
      console.log('Manually refreshing user ID for:', userEmail);
      const userData = await fetchUserId(userEmail);
      if (userData) {
        setUserId(userData.id);
        // If backend has full_name, update userName and localStorage
        if (userData.full_name) {
          console.log('Updating userName from backend full_name:', userData.full_name);
          setUserName(userData.full_name);
        }
        // Update localStorage
        const savedUser = localStorage.getItem('chatbotUser');
        if (savedUser) {
          const user = JSON.parse(savedUser);
          user.userId = userData.id;
          if (userData.full_name) {
            user.name = userData.full_name;
          }
          localStorage.setItem('chatbotUser', JSON.stringify(user));
        }
        return userData.id;
      }
    }
    return null;
  };

  const value = {
    isUserLoggedIn,
    userName,
    userEmail,
    userId,
    hasPayment,
    paidCategories,
    login,
    logout,
    updatePaymentStatus,
    addPaidCategory,
    refreshUserId
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 